import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import toast from "react-hot-toast";
import Loader from "js/Component/Loader";
import DeleteModal from "js/Component/DeleteModal";
import ModalWrapper from "js/Component/ModalWrapper";
import AddEditFinishingPageModal from "./AddEditFinishingPageModal";
import AddEditEmailAction from "./AddEditEmailAction";
import { createFormField, updateFormField, updateFormFieldOptionImage, duplicateFormFieldOption, fetchAllFormFields, RemoveFormFieldOptionImage, deleteFormField } from "js/Api/forms";
import {
    FREE_QUE_TYPE,
    SINGLE_QUE_TYPE,
    MULTIPLE_QUE_TYPE,
    QuestionTypes,
} from "js/Constants/forms";
import "../../../../css/questionModal.css";
import "../../../../css/customSelect.css";

const CustomSelect = ({
    questionType,
    setQuestionType,
    questionTypesOptions,
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    const selectedOption = questionTypesOptions.find(
        (q) => q.value === questionType
    );

    const handleSelect = (option) => {
        if (!disabled) {
            setQuestionType(option.value);
            setIsOpen(false);
        }
    };

    const handleDisplayClick = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className="custom-select-container">
            <div
                className={`custom-select-display ${disabled ? "disabled" : ""}`}
                onClick={handleDisplayClick}
                style={{
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.6 : 1,
                }}
            >
                <i className={selectedOption?.icon} style={{ marginRight: "8px" }}></i>
                <span style={{ flex: 1 }}>{selectedOption.label}</span>
                <i
                    className="fa fa-caret-down"
                    style={{
                        marginLeft: "8px",
                        cursor: disabled ? "not-allowed" : "pointer",
                    }}
                ></i>
            </div>

            {isOpen && !disabled && (
                <div className="custom-select-dropdown">
                    {questionTypesOptions.map((option) => (
                        <div
                            key={option.value}
                            className="custom-select-option"
                            onClick={() => handleSelect(option)}
                        >
                            <i className={option?.icon} style={{ marginRight: "8px" }}></i>
                            <span>{option.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const ColorDropdown = ({ currentColor, onColorChange, onClose }) => {
    const colors = [
        "#FF6B6B",
        "#4ECDC4",
        "#45B7D1",
        "#96CEB4",
        "#FFEAA7",
        "#DDA0DD",
        "#98D8C8",
        "#F7DC6F",
        "#BB8FCE",
        "#85C1E9",

        "#FF9FF3",
        "#54A0FF",
        "#5F27CD",
        "#00D2D3",
        "#FF9F43",
        "#FFA502",
        "#FF6348",
        "#2ED573",
        "#3742FA",
        "#A4B0BE",
        "#2F3542",
        "#40407A",
        "#706FD3",
        "#F8B500",
        "#F97F51",
        "#6C5CE7",
        "#A29BFE",
        "#FD79A8",
        "#00B894",
        "#00CEC9",
    ];

    return (
        <div
            style={{
                position: "absolute",
                top: "100%",
                right: 0,
                backgroundColor: "#fff",
                border: "1px solid #ddd",
                borderRadius: "4px",
                padding: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                zIndex: 1000,
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: "4px",
                minWidth: "140px",
                maxHeight: "120px",
                overflowY: "auto",
            }}
        >
            {colors.map((color) => (
                <div
                    key={color}
                    onClick={() => onColorChange(color)}
                    style={{
                        width: "20px",
                        height: "20px",
                        backgroundColor: color,
                        border:
                            currentColor === color ? "2px solid #333" : "1px solid #ddd",
                        borderRadius: "3px",
                        cursor: "pointer",
                    }}
                />
            ))}
            {currentColor && (
                <div
                    onClick={() => onColorChange(null)}
                    style={{
                        width: "20px",
                        height: "20px",
                        backgroundColor: "#fff",
                        border: "1px solid #ddd",
                        borderRadius: "3px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px",
                    }}
                >
                    ✕
                </div>
            )}
        </div>
    );
};

const SortableSubOptionItem = ({
    option,
    index,
    onInputChange,
    onImageChange,
    onColorChange,
    onRemove,
    showRemoveButton,
    onAddFinishingPage,
    onEditFinishingPage,
    onAddEmailAction,
    onDuplicate,
    canDuplicate = true,
    formType,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({
            id: option.id,
        });

    const [showMoreMenu, setShowMoreMenu] = useState(false);
    const [showColorDropdown, setShowColorDropdown] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: true });
    const fileInputRef = useRef();
    const moreButtonRef = useRef();

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        display: "flex",
        alignItems: "center",
        width: "100%",
        gap: "16px",
    };

    const hasFinishingPage = option.finishingDesc && option.finishingDesc.trim();
    const hasCustomAction = option.customAction;

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ["image/jpeg", "image/png"];
            const maxSize = 1 * 1024 * 1024; // 1MB

            if (!allowedTypes.includes(file.type)) {
                toast.error("Only PNG and JPG files are allowed.");
                return;
            }

            if (file.size > maxSize) {
                toast.error("Image size must be less than 1 MB.");
                return;
            }

            onImageChange(option.id, file);
        }
    };

    const getImagePreview = () => {
        if (option.image?.name) {
            return URL.createObjectURL(option.image);
        }
        return option.image || null;
    };

    const handleColorPickerToggle = () => {
        setShowColorDropdown(!showColorDropdown);
    };

    const handleColorSave = (color) => {
        if (onColorChange) {
            onColorChange(option.id, color);
        }
        setShowColorDropdown(false);
    };

    const handleColorDropdownClose = () => {
        setShowColorDropdown(false);
    };

    const calculateMenuPosition = () => {
        if (moreButtonRef.current) {
            const buttonRect = moreButtonRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const menuHeight = 120;
            const spaceBelow = viewportHeight - buttonRect.bottom;
            const spaceAbove = buttonRect.top;

            if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
                setMenuPosition({ top: false });
            } else {
                setMenuPosition({ top: true });
            }
        }
    };

    const handleMoreMenuToggle = () => {
        if (!showMoreMenu) {
            calculateMenuPosition();
        }
        setShowMoreMenu(!showMoreMenu);
    };

    const handleFinishingPageAction = () => {
        setShowMoreMenu(false);
        if (hasFinishingPage) {
            onEditFinishingPage(option.id);
        } else {
            onAddFinishingPage(option.id);
        }
    };

    const handleEmailAction = () => {
        setShowMoreMenu(false);
        onAddEmailAction(option.id);
    };

    const handleImagePick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = null;
            fileInputRef.current.click();
        }
    };

    const handleRemoveOption = () => {
        onRemove(option.id);
        setShowMoreMenu(false);
    };

    const handleDuplicate = () => {
        setShowMoreMenu(false);
        if (onDuplicate) {
            onDuplicate(option.id);
        }
    };

    return (
        <div {...listeners} ref={setNodeRef} style={style} {...attributes}>
            <i className="fa fa-circle-o" aria-hidden="true" />

            <input
                type="text"
                value={option.text || ""}
                onChange={(e) => onInputChange(option.id, e.target.value)}
                placeholder={`Sub-option ${index + 1}`}
                style={{
                    padding: "5px",
                    width: "250px",
                }}
            />

            <div style={{ flex: 1 }}></div>

            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                {hasFinishingPage && (
                    <i
                        className="fa fa-flag-checkered"
                        style={{
                            fontSize: "16px",
                            color: "#4caf50",
                        }}
                        title="Has finishing page"
                    />
                )}

                {hasCustomAction && <>
                    <i
                        className="fa fa-envelope-o"
                        style={{
                            fontSize: "16px",
                            color: "#4caf50",
                        }}
                        title="Has Email Actions"
                    />
                </>}

                {formType === "single-question" && (
                    <div style={{ position: "relative" }}>
                        {option.bgColorCode ? (
                            <div
                                style={{
                                    width: "16px",
                                    height: "16px",
                                    backgroundColor: option.bgColorCode,
                                    border: "1px solid #ddd",
                                    borderRadius: "3px",
                                    cursor: "pointer",
                                }}
                                onClick={handleColorPickerToggle}
                                title={`Current color: ${option.bgColorCode}`}
                            />
                        ) : (
                            <i
                                className="fa fa-paint-brush"
                                style={{
                                    fontSize: "16px",
                                    color: "#999",
                                    cursor: "pointer",
                                }}
                                onClick={handleColorPickerToggle}
                                title="Set color"
                            />
                        )}

                        {showColorDropdown && (
                            <>
                                <div
                                    style={{
                                        position: "fixed",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        zIndex: 999,
                                    }}
                                    onClick={handleColorDropdownClose}
                                />
                                <ColorDropdown
                                    currentColor={option.bgColorCode}
                                    onColorChange={handleColorSave}
                                    onClose={handleColorDropdownClose}
                                />
                            </>
                        )}
                    </div>
                )}

                <div
                    style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                    onClick={handleImagePick}
                    title={option.image ? "Change image" : "Add image"}
                >
                    {getImagePreview() ? (
                        <img
                            src={getImagePreview()}
                            alt="Sub-option preview"
                            style={{
                                width: "16px",
                                height: "16px",
                                objectFit: "cover",
                                borderRadius: "2px",
                                border: "1px solid #ddd",
                            }}
                        />
                    ) : (
                        <i
                            className="fa fa-image"
                            style={{
                                fontSize: "16px",
                                color: "#999",
                            }}
                        />
                    )}
                </div>

                <div style={{ position: "relative" }}>
                    <i
                        ref={moreButtonRef}
                        className="fa fa-ellipsis-v"
                        style={{
                            fontSize: "16px",
                            color: "#999",
                            cursor: "pointer",
                            padding: "5px"
                        }}
                        onClick={handleMoreMenuToggle}
                        title="More options"
                    />

                    {showMoreMenu && (
                        <>
                            <div
                                style={{
                                    position: "fixed",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    zIndex: 9998,
                                }}
                                onClick={() => setShowMoreMenu(false)}
                            />
                            <div
                                style={{
                                    position: "fixed",
                                    ...(menuPosition.top
                                        ? {
                                            top:
                                                moreButtonRef.current?.getBoundingClientRect()
                                                    .bottom +
                                                4 +
                                                "px",
                                        }
                                        : {
                                            bottom:
                                                window.innerHeight -
                                                moreButtonRef.current?.getBoundingClientRect().top +
                                                4 +
                                                "px",
                                        }),
                                    left:
                                        moreButtonRef.current?.getBoundingClientRect().right -
                                        160 +
                                        "px",
                                    backgroundColor: "#fff",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "4px",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                    zIndex: 9999,
                                    minWidth: "160px",
                                }}
                            >
                                {canDuplicate && (
                                    <div
                                        onClick={handleDuplicate}
                                        style={{
                                            padding: "8px 12px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            borderBottom: "1px solid #f0f0f0",
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.target.style.backgroundColor = "#f5f5f5")
                                        }
                                        onMouseLeave={(e) =>
                                            (e.target.style.backgroundColor = "#fff")
                                        }
                                    >
                                        <i
                                            className="fa fa-copy"
                                            style={{ marginRight: "8px", color: "#666" }}
                                        />
                                        Duplicate
                                    </div>
                                )}

                                {showRemoveButton && (
                                    <div
                                        onClick={handleRemoveOption}
                                        style={{
                                            padding: "8px 12px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            borderBottom: "1px solid #f0f0f0",
                                        }}
                                        onMouseEnter={(e) =>
                                            (e.target.style.backgroundColor = "#f5f5f5")
                                        }
                                        onMouseLeave={(e) =>
                                            (e.target.style.backgroundColor = "#fff")
                                        }
                                    >
                                        <i
                                            className="fa fa-trash"
                                            style={{ marginRight: "8px", color: "#666" }}
                                        />
                                        Remove
                                    </div>
                                )}

                                <div
                                    onClick={handleFinishingPageAction}
                                    style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                    }}
                                    onMouseEnter={(e) =>
                                        (e.target.style.backgroundColor = "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                        (e.target.style.backgroundColor = "#fff")
                                    }
                                >
                                    <i
                                        className="fa fa-flag-checkered"
                                        style={{ marginRight: "8px", color: "#666" }}
                                    />
                                    {hasFinishingPage
                                        ? "Edit finishing page"
                                        : "Add finishing page"}
                                </div>

                                <div
                                    onClick={handleEmailAction}
                                    style={{
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                    }}
                                    onMouseEnter={(e) =>
                                        (e.target.style.backgroundColor = "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                        (e.target.style.backgroundColor = "#fff")
                                    }
                                >
                                    <i
                                        className="fa fa-envelope-o"
                                        style={{ marginRight: "8px", color: "#666" }}
                                    />
                                    {hasCustomAction ? "Edit email action" : "Add email action"}
                                </div>

                            </div>
                        </>
                    )}
                </div>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageChange}
            />
        </div>
    );
};

const AddEditSubQuestionModal = (props) => {
    const {
        isVisible,
        editAbleQuestionObj,
        onClose,
        SelectedOptionId,
        refetchQuestionsList,
        formType,
        subQuestionList,
        currentEditingSubQuestion,
        formId,
        standalone = false,
        onDraftSave,
        subQuestionDrafts,
        customEmailText,
    } = props;

    const [question, setQuestion] = useState("");
    const [questionType, setQuestionType] = useState(MULTIPLE_QUE_TYPE.id);
    const [isLoading, setIsLoading] = useState(false);
    const [isMandatory, setIsMandatory] = useState(false);
    const [isDeleteFormModal, setIsDeleteFormModal] = useState(false);
    const [editAbleSubQuestionObj, setEditAbleSubQuestionObj] = useState(null);
    const [options, setOptions] = useState([]);

    const [isFinishingPageModalVisible, setIsFinishingPageModalVisible] =
        useState(false);
    const [selectedOptionForFinishingPage, setSelectedOptionForFinishingPage] =
        useState(null);

    const [isEmailActionVisible, setIsEmailActionVisible] = useState(false);
    const [selectedOptionForEmailAction, setSelectedOptionForEmailAction] =
        useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const createDefaultOptions = useCallback(() => {
        const timestamp = Date.now();
        return [
            {
                id: `new-${timestamp}-1`,
                text: "",
                image: null,
                bgColorCode: null,
                finishingDesc: "",
                finishingImage: null,
            },
            {
                id: `new-${timestamp}-2`,
                text: "",
                image: null,
                bgColorCode: null,
                finishingDesc: "",
                finishingImage: null,
            },
        ];
    }, []);

    const handleOptionColorChange = (optionId, color) => {
        setOptions((prev) =>
            prev.map((opt) =>
                opt.id === optionId ? { ...opt, bgColorCode: color } : opt
            )
        );
    };

    const handleAddEmailAction = (optionId) => {
        const option = options.find((opt) => opt.id === optionId);
        setSelectedOptionForEmailAction(option);
        setIsEmailActionVisible(true);
    };

    const handleCloseEmailAction = () => {
        setIsEmailActionVisible(false);
        setSelectedOptionForEmailAction(null);
    };

    const onEmailSubmitAction = (optionId, emailData) => {
        setOptions((prev) =>
            prev.map((opt) =>
                opt.id === optionId
                    ? {
                        ...opt,
                        customAction: JSON.stringify(emailData),
                    }
                    : opt
            )
        );
    };

    const handleDuplicateSubOption = async (optionId) => {
        const option = options.find((opt) => opt.id === optionId);
        if (!option) return;

        if (!optionId.startsWith("new-") && editAbleSubQuestionObj?.id) {
            try {
                toast.loading("Duplicating sub-option...");
                const response = await duplicateFormFieldOption(optionId);

                if (response?.success) {
                    toast.dismiss();
                    toast.success("Sub-option duplicated successfully");
                    refetchQuestionsList();

                    try {
                        const updatedResponse = await fetchAllFormFields(
                            formId || editAbleQuestionObj?.formId
                        );
                        if (updatedResponse?.success && updatedResponse?.data) {
                            const updatedSubQuestion = updatedResponse.data.find(
                                (field) => field.id === editAbleSubQuestionObj.id
                            );

                            if (updatedSubQuestion && updatedSubQuestion.fieldOptions) {
                                const updatedOptions = updatedSubQuestion.fieldOptions
                                    .sort((a, b) => a.optionOrder - b.optionOrder)
                                    .map((option) => ({
                                        id: option.id,
                                        text: option.fieldOption,
                                        image: option.imageURL ?? null,
                                        finishingDesc: option.finishingDesc ?? "",
                                        finishingImage: option.finishingImageURL ?? null,
                                        customAction: option.customAction ?? null,
                                    }));

                                setOptions(updatedOptions);

                                setEditAbleSubQuestionObj(updatedSubQuestion);
                            }
                        }
                    } catch (refreshError) {
                        console.error("Error refreshing sub-question data:", refreshError);
                    }
                } else {
                    toast.dismiss();
                    toast.error(response?.error || "Failed to duplicate sub-option");
                }
            } catch (error) {
                toast.dismiss();
                toast.error("Failed to duplicate sub-option");
                console.error("Error duplicating sub-option:", error);
            }
        } else {
            const duplicatedOption = {
                ...option,
                id: `new-${Date.now()}-${Math.random()}`,
                text: option.text,
                image: option.image ? option.image : null,
                finishingImage: option.finishingImage ? option.finishingImage : null,
                finishingDesc: option.finishingDesc,
                customAction: option.customAction,
            };

            const optionIndex = options.findIndex((opt) => opt.id === optionId);
            setOptions((prev) => [
                ...prev.slice(0, optionIndex + 1),
                duplicatedOption,
                ...prev.slice(optionIndex + 1),
            ]);

            toast.success("Sub-option duplicated locally");
        }
    };

    useEffect(() => {
        if (isVisible) {
            if (currentEditingSubQuestion) {
                setEditAbleSubQuestionObj(currentEditingSubQuestion);
                setQuestion(currentEditingSubQuestion.fieldName);
                setQuestionType(
                    formType === "single-question"
                        ? SINGLE_QUE_TYPE.id
                        : currentEditingSubQuestion.fieldType
                );

                setIsMandatory(
                    formType === "single-question" || formType === "checklist"
                        ? true
                        : currentEditingSubQuestion.mandatory || false
                );

                if (
                    currentEditingSubQuestion.fieldOptions &&
                    currentEditingSubQuestion.fieldOptions.length > 0
                ) {
                    const loadedOptions = currentEditingSubQuestion.fieldOptions.map(
                        (obj) => ({
                            id: obj.id,
                            text: obj.fieldOption,
                            image: obj.imageURL ? obj.imageURL : null,
                            bgColorCode: obj.bgColorCode ?? null,
                            finishingDesc: obj.finishingDesc || "",
                            finishingImage: obj.finishingImageURL || null,
                            customAction: obj.customAction ?? null,
                        })
                    );
                    setOptions(loadedOptions);
                } else {
                    setOptions(createDefaultOptions());
                }
            } else if (SelectedOptionId) {
                if (subQuestionDrafts && subQuestionDrafts[SelectedOptionId]) {
                    // console.log("Loading sub-question from draft:", subQuestionDrafts[SelectedOptionId]);
                    const draft = subQuestionDrafts[SelectedOptionId];

                    setEditAbleSubQuestionObj(null);
                    setQuestion(draft.fieldName);
                    setQuestionType(
                        formType === "single-question"
                            ? SINGLE_QUE_TYPE.id
                            : draft.fieldType
                    );
                    setIsMandatory(
                        formType === "single-question" || formType === "checklist"
                    );

                    const draftOptions =
                        draft.fieldOptions?.map((opt, index) => ({
                            id: opt.id || `draft-${Date.now()}-${index}`,
                            text: opt.fieldOption,
                            image: opt.image || null,
                            bgColorCode: opt.bgColorCode ?? null,
                            finishingDesc: opt.finishingDesc || "",
                            finishingImage: opt.finishingImage || null,
                            customAction: opt.customAction ?? null,
                        })) || createDefaultOptions();

                    setOptions(draftOptions);
                } else if (subQuestionList && subQuestionList.length > 0) {
                    const existingSubQuestion = subQuestionList.find(
                        (subQ) => subQ.refFieldOption === SelectedOptionId
                    );

                    if (existingSubQuestion) {
                        setEditAbleSubQuestionObj(existingSubQuestion);
                        setQuestion(existingSubQuestion.fieldName);
                        setQuestionType(
                            formType === "single-question"
                                ? SINGLE_QUE_TYPE.id
                                : existingSubQuestion.fieldType
                        );

                        setIsMandatory(
                            formType === "single-question" || formType === "checklist"
                                ? true
                                : existingSubQuestion.mandatory || false
                        );

                        if (
                            existingSubQuestion.fieldOptions &&
                            existingSubQuestion.fieldOptions.length > 0
                        ) {
                            const loadedOptions = existingSubQuestion.fieldOptions.map(
                                (obj) => ({
                                    id: obj.id,
                                    text: obj.fieldOption,
                                    image: obj.imageURL ? obj.imageURL : null,
                                    bgColorCode: obj.bgColorCode ?? null,
                                    finishingDesc: obj.finishingDesc || "",
                                    finishingImage: obj.finishingImageURL || null,
                                    customAction: obj.customAction ?? null,
                                })
                            );
                            setOptions(loadedOptions);
                        } else {
                            setOptions(createDefaultOptions());
                        }
                    } else {
                        resetForm();
                    }
                } else {
                    resetForm();
                }
            }
        }
    }, [
        isVisible,
        SelectedOptionId,
        subQuestionList,
        currentEditingSubQuestion,
        createDefaultOptions,
        formType,
        subQuestionDrafts,
    ]);

    const resetForm = () => {
        setEditAbleSubQuestionObj(null);
        setQuestion("");
        setQuestionType(
            formType === "single-question" ? SINGLE_QUE_TYPE.id : MULTIPLE_QUE_TYPE.id
        );
        setIsMandatory(formType === "single-question" || formType === "checklist");
        setOptions(createDefaultOptions());
    };

    const shouldShowAddSubOptionButton = () => {
        const maxOptions = getMaxSubOptionsCount();
        return options.length < maxOptions;
    };

    const isFormValid = useCallback(() => {
        if (!question || question.trim().length < 2) return false;
        if (questionType === FREE_QUE_TYPE.id) return true;

        const maxOptions = getMaxSubOptionsCount();

        if (options.length > maxOptions) return false;

        return options.length >= 2 && options.every((o) => o.text && o.text.trim());
    }, [question, questionType, options]);

    const handleOptionInputChange = (optionId, value) => {
        setOptions((prev) =>
            prev.map((opt) => (opt.id === optionId ? { ...opt, text: value } : opt))
        );
    };

    const handleOptionImageChange = (optionId, file) => {
        setOptions((prev) =>
            prev.map((opt) => (opt.id === optionId ? { ...opt, image: file } : opt))
        );
    };

    const handleRemoveOption = (optionId) => {
        if (options.length <= 2) {
            toast.error("At least 2 options are required");
            return;
        }
        setOptions((prev) => prev.filter((opt) => opt.id !== optionId));
    };

    const getMaxSubOptionsCount = () => {
        return 6;
    };

    const handleAddOption = () => {
        const maxOptions = getMaxSubOptionsCount();

        if (options.length >= maxOptions) {
            toast.error("Sub-questions can have maximum 6 options");
            return;
        }
        const newOption = {
            id: `new-${Date.now()}-${Math.random()}`,
            text: "",
            image: null,
            finishingDesc: "",
            finishingImage: null,
            customAction: null,
        };
        setOptions((prev) => [...prev, newOption]);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setOptions((prev) => {
            const oldIndex = prev.findIndex((opt) => opt.id === active.id);
            const newIndex = prev.findIndex((opt) => opt.id === over.id);

            if (oldIndex === -1 || newIndex === -1) return prev;
            return arrayMove(prev, oldIndex, newIndex);
        });
    };

    const handleAddFinishingPage = (optionId) => {
        const option = options.find((opt) => opt.id === optionId);
        setSelectedOptionForFinishingPage(option);
        setIsFinishingPageModalVisible(true);
    };

    const handleEditFinishingPage = (optionId) => {
        const option = options.find((opt) => opt.id === optionId);
        setSelectedOptionForFinishingPage(option);
        setIsFinishingPageModalVisible(true);
    };

    const handleFinishingPageSave = (optionId, finishingData) => {
        setOptions((prev) =>
            prev.map((opt) =>
                opt.id === optionId
                    ? {
                        ...opt,
                        finishingDesc: finishingData.finishingDesc,
                        finishingImage: finishingData.finishingImage,
                    }
                    : opt
            )
        );
    };

    const handleFinishingPageClose = () => {
        setIsFinishingPageModalVisible(false);
        setSelectedOptionForFinishingPage(null);
    };

    const handleSave = async () => {
        if (!isFormValid()) {
            toast.error("Please fill in all required fields");
            return;
        }

        if (!standalone && typeof onDraftSave === "function") {
            onDraftSave(SelectedOptionId, {
                fieldName: question.trim(),
                fieldType:
                    formType === "single-question" ? SINGLE_QUE_TYPE.id : questionType,
                mandatory: isMandatory,
                fieldOptions: options.map((o, i) => ({
                    fieldOption: o.text.trim(),
                    optionOrder: i + 1,
                    image: o.image || null,
                    bgColorCode: o.bgColorCode ?? null,
                    finishingDesc: o.finishingDesc?.trim() || null,
                    finishingImage: o.finishingImage || null,
                    customAction: o.customAction || null,
                })),
            });
            handleClose();
            return;
        }

        setIsLoading(true);

        try {
            if (editAbleSubQuestionObj?.id) {
                await handleUpdate();
            } else {
                await handleCreate();
            }
        } catch (error) {
            console.error("Error saving sub-question:", error);
            toast.error("Failed to save sub-question");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreate = async () => {
        const storedUserInfo = localStorage.getItem("userInfo") ?? "{}";
        const parsedUserInfo = JSON.parse(storedUserInfo);

        const fieldOptions = options
            .filter((opt) => opt.text && opt.text.trim())
            .map((obj, index) => {
                const optionData = {
                    fieldOption: obj.text.trim(),
                    optionOrder: index + 1,
                };

                if (formType === "single-question" && obj.bgColorCode) {
                    optionData.bgColorCode = obj.bgColorCode;
                }

                if (obj.finishingDesc && obj.finishingDesc.trim()) {
                    optionData.finishingDesc = obj.finishingDesc.trim();
                }

                if (obj.customAction) {
                    optionData.customAction = obj.customAction;
                }

                return optionData;
            });

        const payload = {
            formId: editAbleQuestionObj?.formId || formId,
            fieldName: question?.trim(),
            fieldType: questionType,
            fieldOrder: 1,
            userID: parsedUserInfo?.userId,
            mandatory: isMandatory,
            refFieldOption: SelectedOptionId,
        };

        if (questionType !== FREE_QUE_TYPE.id) {
            payload.fieldOptions = fieldOptions;
        }

        const response = await createFormField(payload);
        if (response?.success) {
            await handleImageUploads(response.data);
            toast.success("Sub-question created successfully");
            refetchQuestionsList();
            handleClose();
        } else {
            toast.error(response?.error || "Failed to create sub-question");
        }
    };

    const handleUpdate = async () => {
        const storedUserInfo = localStorage.getItem("userInfo") ?? "{}";
        const parsedUserInfo = JSON.parse(storedUserInfo);

        const originalIds =
            editAbleSubQuestionObj.fieldOptions?.map((opt) => opt.id) || [];
        const editedIds = options.map((opt) => opt.id);
        const removedIds = originalIds.filter((id) => !editedIds.includes(id));

        const preparedOptions = options
            .filter((opt) => opt.text && opt.text.trim())
            .map((option, index) => {
                const optionData = {
                    fieldOption: option.text.trim(),
                    optionOrder: index + 1,
                };

                if (formType === "single-question" && option.bgColorCode) {
                    optionData.bgColorCode = option.bgColorCode;
                }

                if (option.finishingDesc && option.finishingDesc.trim()) {
                    optionData.finishingDesc = option.finishingDesc.trim();
                }

                if (option.customAction) {
                    optionData.customAction = option.customAction;
                }

                if (option.id.toString().startsWith("new-")) {
                    return optionData;
                } else {
                    return { id: option.id, ...optionData };
                }
            });

        const payload = {
            id: editAbleSubQuestionObj.id,
            formId: editAbleQuestionObj?.formId || formId,
            fieldName: question?.trim(),
            fieldType: questionType,
            userID: parsedUserInfo?.userId,
            mandatory: isMandatory,
            removeFieldOptionIds: removedIds,
        };

        if (questionType !== FREE_QUE_TYPE.id) {
            payload.fieldOptions = preparedOptions;
        }

        const response = await updateFormField(payload);
        if (response?.success) {
            await handleImageUploads(response.data);
            toast.success("Sub-question updated successfully");
            refetchQuestionsList();
            handleClose();
        } else {
            toast.error(response?.error || "Failed to update sub-question");
        }
    };

    const handleImageUploads = async (responseData) => {
        const fieldOptionsResponse = responseData?.fieldOptions || [];

        const optionImagePromises = options
            .map((opt, index) => {
                if (opt.image && opt.image.name) {
                    const matchingOption = fieldOptionsResponse.find(
                        (resOpt) => resOpt.optionOrder === index + 1
                    );
                    if (matchingOption) {
                        return updateFormFieldOptionImage(
                            matchingOption.id,
                            opt.image,
                            false
                        );
                    }
                }
                return null;
            })
            .filter(Boolean);

        const finishingImagePromises = options
            .map((opt, index) => {
                if ((opt.finishingImage && opt.finishingImage.name) || opt.finishingImage === "remove") {
                    const matchingOption = fieldOptionsResponse.find(
                        (resOpt) => resOpt.optionOrder === index + 1
                    );
                    if (matchingOption) {
                        if (opt.finishingImage === "remove") {
                            return RemoveFormFieldOptionImage(
                                matchingOption.id,
                                true
                            );
                        }
                        else {
                            return updateFormFieldOptionImage(
                                matchingOption.id,
                                opt.finishingImage,
                                true
                            );
                        }
                    }
                }
                return null;
            })
            .filter(Boolean);

        const allPromises = [...optionImagePromises, ...finishingImagePromises];
        if (allPromises.length > 0) {
            await Promise.all(allPromises);
        }
    };

    const handleDelete = async () => {
        if (!editAbleSubQuestionObj?.id) return;

        try {
            const response = await deleteFormField(editAbleSubQuestionObj.id);
            if (response?.success) {
                toast.success("Question deleted successfully");
                refetchQuestionsList();
                setIsDeleteFormModal(false);
                handleClose();
            } else {
                toast.error(response?.error || "Failed to delete question");
            }
        } catch (error) {
            console.error("Error deleting question:", error);
            toast.error("Failed to delete question");
        }
        setIsDeleteFormModal(false);
        handleClose();
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const mandatoryToggleHandler = (e) => {
        setIsMandatory(e.target.checked);
    };

    const onDeletePressHandler = () => {
        setIsDeleteFormModal(true);
    };

    const onCancelHandler = () => {
        handleClose();
    };

    const onCloseDeleteModal = () => {
        setIsDeleteFormModal(false);
    };

    if (!isVisible) return null;

    const saveButtonEnabled = isFormValid();

    return (
        <>
            <ModalWrapper>
                <div className="modal-header">
                    <h1 className="modal-title">
                        {editAbleSubQuestionObj?.id
                            ? "Edit sub-question"
                            : "Add sub-question"}
                    </h1>
                    <button
                        className="close-button"
                        disabled={isLoading}
                        onClick={handleClose}
                    >
                        &times;
                    </button>
                </div>

                <div
                    className="modal-body"
                    style={{ flex: 1, overflowY: "auto", padding: "20px" }}
                >
                    <label className="input-label">Sub-Question Title</label>
                    <input
                        className="question-title-input"
                        type="text"
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        placeholder="Untitled sub question"
                    />

                    <div
                        style={{ gap: 20 }}
                        className="d-flex align-items-center mt-3 gap-2"
                    >
                        <CustomSelect
                            questionTypesOptions={QuestionTypes}
                            questionType={questionType}
                            setQuestionType={(value) => {
                                setQuestionType(value);
                            }}
                            disabled={formType === "single-question"}
                        />

                        <div className="form-check form-check-inline">
                            <input
                                style={{ width: "20px", height: "20px" }}
                                className="form-check-input"
                                type="checkbox"
                                id="subQuestionMandatory"
                                onChange={mandatoryToggleHandler}
                                checked={isMandatory}
                                disabled={
                                    formType === "single-question" || formType === "checklist"
                                }
                            />
                            <label
                                className="form-check-label ml-1"
                                htmlFor="subQuestionMandatory"
                                style={{
                                    opacity:
                                        formType === "single-question" || formType === "checklist"
                                            ? 0.6
                                            : 1,
                                    cursor:
                                        formType === "single-question" || formType === "checklist"
                                            ? "not-allowed"
                                            : "pointer",
                                }}
                            >
                                Mandatory
                            </label>
                        </div>
                    </div>

                    {questionType !== FREE_QUE_TYPE.id && (
                        <div style={{ marginTop: "20px" }}>
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={options.map((opt) => opt.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className="options-container">
                                        {options.map((option, index) => (
                                            <SortableSubOptionItem
                                                key={option.id}
                                                option={option}
                                                index={index}
                                                onInputChange={handleOptionInputChange}
                                                onImageChange={handleOptionImageChange}
                                                onColorChange={handleOptionColorChange}
                                                onRemove={handleRemoveOption}
                                                showRemoveButton={options.length > 2}
                                                onAddFinishingPage={handleAddFinishingPage}
                                                onEditFinishingPage={handleEditFinishingPage}
                                                onAddEmailAction={handleAddEmailAction}
                                                onDuplicate={handleDuplicateSubOption}
                                                canDuplicate={true}
                                                formType={formType}
                                            />
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>

                            {shouldShowAddSubOptionButton() && (
                                <button onClick={handleAddOption} className="add-option-btn">
                                    + Add sub-option
                                </button>
                            )}
                        </div>
                    )}

                    <div className="modal-actions">
                        {editAbleSubQuestionObj?.id ? (
                            <button
                                className="delete-btn btn btn-outline-danger"
                                disabled={isLoading}
                                onClick={onDeletePressHandler}
                            >
                                Delete
                            </button>
                        ) : (
                            <></>
                        )}
                        <button
                            className="cancel-btn"
                            disabled={isLoading}
                            onClick={onCancelHandler}
                        >
                            Cancel
                        </button>
                        {isLoading ? (
                            <Loader style={{ marginLeft: 20 }} />
                        ) : (
                            <button
                                className="save-btn"
                                onClick={handleSave}
                                disabled={!saveButtonEnabled}
                                style={{
                                    cursor: saveButtonEnabled ? "pointer" : "not-allowed",
                                    opacity: saveButtonEnabled ? 1 : 0.6,
                                }}
                            >
                                Save
                            </button>
                        )}
                    </div>
                </div>
            </ModalWrapper>

            <DeleteModal
                visible={isDeleteFormModal}
                onClose={onCloseDeleteModal}
                onCancel={onCloseDeleteModal}
                onDelete={handleDelete}
                title="Are you sure you want to delete this sub question?"
            />

            <AddEditFinishingPageModal
                isVisible={isFinishingPageModalVisible}
                selectedOption={selectedOptionForFinishingPage}
                onClose={handleFinishingPageClose}
                onSave={handleFinishingPageSave}
            />

            <AddEditEmailAction
                visible={isEmailActionVisible}
                onClose={handleCloseEmailAction}
                onCancel={handleCloseEmailAction}
                selectedOption={selectedOptionForEmailAction}
                onEmailSubmitAction={onEmailSubmitAction}
                customEmailText={customEmailText}
                questionText={question}
            />
        </>
    );
};

export default memo(AddEditSubQuestionModal);
